import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import config from '../config/config';
import { trackLogin, trackLogout, trackUserRole, trackError } from '../utils/analytics';
import { isPWA, showLocalNotification, performLogoutCleanup } from '../utils/pwa';

// Configure axios defaults
axios.defaults.baseURL = config.API_BASE_URL;
axios.defaults.timeout = 10000;

console.log('Axios configured with base URL:', config.API_BASE_URL);

// Add response interceptor to handle auth errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('Received 401 response, clearing auth state');
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      // Don't redirect here as it might cause issues, let components handle it
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Set up axios interceptor for JWT token and online/offline detection
  useEffect(() => {
    const token = localStorage.getItem('token');
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    console.log('Initial token check:', token ? 'Token found' : 'No token');
    
    if (token && token !== 'undefined' && token !== 'null') {
      // Check if token is expired
      if (tokenExpiry && new Date().getTime() > parseInt(tokenExpiry)) {
        console.log('Token expired, clearing auth state');
        localStorage.removeItem('token');
        localStorage.removeItem('tokenExpiry');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setLoading(false);
        return;
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('Set authorization header:', axios.defaults.headers.common['Authorization']);
      
      // Try to load user from localStorage first for faster startup
      const storedUser = localStorage.getItem('user');
      if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          console.log('Loaded user from localStorage:', userData.email);
        } catch (error) {
          console.error('Failed to parse stored user data:', error);
          localStorage.removeItem('user');
        }
      }
      
      // Verify token is still valid
      verifyToken();
    } else {
      console.log('No valid token found, clearing any existing auth headers');
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }

    // Online/Offline detection for PWA
    const handleOnline = () => {
      setIsOnline(true);
      if (isPWA()) {
        showLocalNotification('Back Online', {
          body: 'CalorieNSweat is now connected to the internet',
          tag: 'connectivity'
        });
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (isPWA()) {
        showLocalNotification('Offline Mode', {
          body: 'CalorieNSweat is now running in offline mode',
          tag: 'connectivity'
        });
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const verifyToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found in localStorage');
        setLoading(false);
        return;
      }
      
      console.log('Verifying token with headers:', axios.defaults.headers.common);
      const response = await axios.get('/api/auth/me');
      console.log('Token verification successful:', response.data);
      
      const userData = response.data;
      setUser(userData);
      
      // Store user data in localStorage for faster subsequent loads
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Track user role information
      trackUserRole(
        userData.memberRole?.displayName || 'No Role',
        userData.systemRoles?.map(role => role.displayName) || []
      );
    } catch (error) {
      console.error('Token verification failed:', error);
      console.error('Error response:', error.response?.status, error.response?.data);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, recaptchaToken) => {
    try {
      console.log('Attempting login with:', email);
      console.log('reCAPTCHA token:', recaptchaToken ? 'Present' : 'Missing');
      console.log('Request URL:', `${axios.defaults.baseURL}/api/auth/login`);
      
      const response = await axios.post('/api/auth/login', {
        email,
        password,
        recaptchaToken
      });

      console.log('Login response:', response.data);
      
      // Handle different possible response structures
      let token, userData;
      if (response.data.token && response.data.user) {
        // Standard structure: { token: "...", user: {...} }
        token = response.data.token;
        userData = response.data.user;
      } else if (response.data.access_token) {
        // Alternative structure: { access_token: "...", user: {...} }
        token = response.data.access_token;
        userData = response.data.user || response.data;
      } else if (typeof response.data === 'string') {
        // Token only response
        token = response.data;
        userData = null; // Will be fetched separately
      } else {
        console.error('Unexpected login response structure:', response.data);
        throw new Error('Invalid login response format');
      }
      
      if (!token) {
        console.error('No token found in response:', response.data);
        throw new Error('No authentication token received');
      }
      
      console.log('Extracted token:', token ? 'Present' : 'Missing');
      console.log('Extracted user data:', userData ? 'Present' : 'Missing');
      
      // Calculate token expiry (30 days from now)
      const expiryTime = new Date().getTime() + (30 * 24 * 60 * 60 * 1000);
      
      localStorage.setItem('token', token);
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('Token set in axios headers:', axios.defaults.headers.common['Authorization']);
      console.log('Token will expire on:', new Date(expiryTime).toLocaleString());
      
      // If we have user data, set it directly, otherwise fetch it
      if (userData) {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Track successful login
        trackLogin('email');
        trackUserRole(
          userData.memberRole?.displayName || 'No Role',
          userData.systemRoles?.map(role => role.displayName) || []
        );
      } else {
        // Fetch user data with the new token
        await verifyToken();
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      console.error('Login error details:', error.response?.status, error.response?.data);
      
      // Clean up on login failure
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      
      // Track login error
      trackError('Login Failed', error.response?.data?.message || error.message || 'Login failed', 'login');
      
      // Extract specific error message from backend
      let errorMessage = 'Login failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    // Track logout event
    trackLogout();
    
    try {
      // Clear authentication state
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      
      // Perform complete cleanup for PWA
      await performLogoutCleanup();
      
      // Force redirect to login page (bypass any cached routes)
      window.location.replace('/login?t=' + Date.now());
    } catch (error) {
      console.error('Logout cleanup failed:', error);
      // Fallback: still clear auth state and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      window.location.replace('/login');
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isOnline
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
